import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from './client'

// ============================================================
//  Types
// ============================================================

interface DiffEntry {
  entityType: string
  amazonId: string
  name: string
  action: 'added' | 'modified' | 'removed'
  changes?: Record<string, { old: unknown; new: unknown }>
}

interface DiffResult {
  added: DiffEntry[]
  modified: DiffEntry[]
  removed: DiffEntry[]
}

interface EntityCounts {
  campaigns: number
  adGroups: number
  keywords: number
  negativeKeywords: number
}

export interface ImportRun {
  id: number
  filename: string
  status: 'uploaded' | 'diffed' | 'applied' | 'failed'
  entityCounts: EntityCounts | null
  diffData: DiffResult | null
  errorMessage: string | null
  appliedAt: string | null
  createdAt: string
}

interface ImportRunsResponse {
  runs: ImportRun[]
  total: number
}

interface ImportResponse {
  import: ImportRun
}

// ============================================================
//  Hooks
// ============================================================

export function useImportRuns(limit = 20, offset = 0) {
  return useQuery({
    queryKey: ['bulk-import-runs', limit, offset],
    queryFn: () => api.get<ImportRunsResponse>(`/bulk-import/runs/?limit=${limit}&offset=${offset}`),
  })
}

export function useImportRun(id: number | null) {
  return useQuery({
    queryKey: ['bulk-import-run', id],
    queryFn: () => api.get<ImportResponse>(`/bulk-import/${id}/`),
    enabled: id !== null,
  })
}

export function useUploadBulkSheet() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      return api.upload<ImportResponse>('/bulk-import/upload/', formData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-import-runs'] })
    },
  })
}

export function useApplyImport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (importId: number) =>
      api.post<ImportResponse>(`/bulk-import/${importId}/apply/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-import-runs'] })
      queryClient.invalidateQueries({ queryKey: ['bulk-import-run'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['keywords'] })
    },
  })
}

export function useExportXlsx() {
  return useMutation({
    mutationFn: async () => {
      const blob = await api.download('/bulk-import/export/')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'amazon_ads_export.xlsx'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  })
}
